import React, { ReactElement } from 'react'

import DeviceFiltering from './DeviceFiltering'
import ContentRestriction from './ContentRestriction'
import CSVExportFormat from './CSVExportFormat'
import StatisticCalculations from './StatisticCalculations'
import Audit from './Audit'
// CSS imports. Commented out because of current build process.
// import './advancedTab.scss'

/**
 * Component properties type.
 */
type Props = {
    isWallboardReport: boolean
    isExternalReport: boolean
    includeCallsForGrpDevEnabled: boolean
    includeCallsForGrpDev: boolean
    setIncludeCallsForGrpDev: (checked: boolean) => void
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled: boolean
    calcACDStatsForAgntsSignedIntoSpecGrp: boolean
    setCalcACDStatsForAgntsSignedIntoSpecGrp: (checked: boolean) => void
    considerCallsMvdToUnresAsOverflwd: boolean
    setConsiderCallsMvdToUnresAsOverflwd: (checked: boolean) => void
    maxFilterListLimit: number | undefined
    setMaxFilterListLimit: (newValue: number | undefined) => void
    maxRptRowDisplLimit: number | undefined
    setMaxRptRowDisplLimit: (newValue: number | undefined) => void
    formatCSVStatValuesType: number
    setFormatCSVStatValuesType: (newValue: number) => void
    enableRptRequestAudit: boolean
    setEnableRptRequestAudit: (checked: boolean) => void
    initialiseRptFilterEntityInfo: () => Promise<void>
    isReportTemplateUser: boolean
    currentTelSysType: number
}

/**
 * Advanced tab of report configuration modal window.
 *
 * @param checkboxEnabled
 * @param isWallboardReport Flag specifying whether current report is a Wallboard.
 * @param isExternalReport Flag specifying whether current report is an External Content Report.
 * @param includeCallsForGrpDevEnabled Flag specifying whether setting corresponding property is allowed.
 * @param includeCallsForGrpDev Report property.
 * @param setIncludeCallsForGrpDev Setter for corresponding report property.
 * @param calcACDStatsForAgntsSignedIntoSpecGrpEnabled Flag specifying whether setting corresponding property is allowed.
 * @param calcACDStatsForAgntsSignedIntoSpecGrp Report property.
 * @param setCalcACDStatsForAgntsSignedIntoSpecGrp Setter for corresponding report property.
 * @param considerCallsMvdToUnresAsOverflwd Report property.
 * @param setConsiderCallsMvdToUnresAsOverflwd Setter for corresponding report property.
 * @param maxFilterListLimit Report property.
 * @param setMaxFilterListLimit Setter for corresponding report property.
 * @param maxRptRowDisplLimit Report property.
 * @param setMaxRptRowDisplLimit Setter for corresponding report property.
 * @param formatCSVStatValuesType Report property.
 * @param setFormatCSVStatValuesType Setter for corresponding report property.
 * @param enableRptRequestAudit Report property.
 * @param setEnableRptRequestAudit Setter for corresponding report property.
 * @param initialiseRptFilterEntityInfo Initialisation method for filter entity data.
 * @param isReportTemplateUser Flag specifying whether current user is a report template user.
 * @param currentTelSysType Report property.
 */
const AdvancedTab: React.FC<Props> = ({
    isWallboardReport,
    isExternalReport,
    includeCallsForGrpDevEnabled,
    includeCallsForGrpDev,
    setIncludeCallsForGrpDev,
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled,
    calcACDStatsForAgntsSignedIntoSpecGrp,
    setCalcACDStatsForAgntsSignedIntoSpecGrp,
    considerCallsMvdToUnresAsOverflwd,
    setConsiderCallsMvdToUnresAsOverflwd,
    maxFilterListLimit,
    setMaxFilterListLimit,
    maxRptRowDisplLimit,
    setMaxRptRowDisplLimit,
    formatCSVStatValuesType,
    setFormatCSVStatValuesType,
    enableRptRequestAudit,
    setEnableRptRequestAudit,
    initialiseRptFilterEntityInfo,
    isReportTemplateUser,
    currentTelSysType
}: Props): ReactElement => (
    <div className='advanced-tab tab-area'>
        {!isExternalReport && (
            <>
                <DeviceFiltering
                    includeCallsForGrpDevEnabled={includeCallsForGrpDevEnabled}
                    calcACDStatsForAgntsSignedIntoSpecGrpEnabled={calcACDStatsForAgntsSignedIntoSpecGrpEnabled}
                    includeCallsForGrpDev={includeCallsForGrpDev}
                    setIncludeCallsForGrpDev={setIncludeCallsForGrpDev}
                    calcACDStatsForAgntsSignedIntoSpecGrp={calcACDStatsForAgntsSignedIntoSpecGrp}
                    setCalcACDStatsForAgntsSignedIntoSpecGrp={setCalcACDStatsForAgntsSignedIntoSpecGrp}
                    currentTelSysType={currentTelSysType}
                />

                <StatisticCalculations
                    considerCallsMvdToUnresAsOverflwd={considerCallsMvdToUnresAsOverflwd}
                    setConsiderCallsMvdToUnresAsOverflwd={setConsiderCallsMvdToUnresAsOverflwd}
                />

                <ContentRestriction
                    isWallboardReport={isWallboardReport}
                    maxFilterListLimit={maxFilterListLimit}
                    setMaxFilterListLimit={setMaxFilterListLimit}
                    maxRptRowDisplLimit={maxRptRowDisplLimit}
                    setMaxRptRowDisplLimit={setMaxRptRowDisplLimit}
                    initialiseRptFilterEntityInfo={initialiseRptFilterEntityInfo}
                />
                {!isReportTemplateUser ? (
                    <CSVExportFormat
                        formatCSVStatValuesType={formatCSVStatValuesType}
                        setFormatCSVStatValuesType={setFormatCSVStatValuesType}
                    />
                ) : (
                    <></>
                )}
            </>
        )}
        <Audit enableRptRequestAudit={enableRptRequestAudit} setEnableRptRequestAudit={setEnableRptRequestAudit} />
    </div>
)

export default AdvancedTab
